/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ThemeProvider.tsx
 🗓️ Created: 2025-01-05
 🧩 Description:
    Theme provider component for managing application-wide theme state
    and providing theme context to child components.

 🔧 Notes:
    - Implements React Context for theme state management
    - Integrates with theme store for centralized theme control
    - Provides theme context to all child components
    - Handles theme initialization and updates
    - Supports dynamic theme switching and persistence
    - Ensures consistent theming across the application

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useThemeStore } from '../store/theme';

interface ThemeProviderProps {
  children: ReactNode;
}

const ThemeContext = createContext({});

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { themeMode, themePrefs, applyTheme } = useThemeStore();

  useEffect(() => {
    applyTheme();
  }, [themeMode, themePrefs, applyTheme]);

  return (
    <ThemeContext.Provider value={{}}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  return useContext(ThemeContext);
};
